import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '@/lib/api/auth';
import { ApiError, type AuthTokens, type AuthUser, type OtpIntent } from '@/lib/api/types';

/**
 * Auth store.
 *
 * Persistence policy: ONLY the refresh token + user shape persist to localStorage.
 * The access token lives in memory only — narrower XSS blast radius.
 * On boot, if a refresh token is present we silently refresh to get a fresh access token.
 */

interface PersistedShape {
  refreshToken: string | null;
  refreshTokenExpiresAt: string | null;
  user: AuthUser | null;
}

interface AuthState extends PersistedShape {
  accessToken: string | null;
  accessTokenExpiresAt: string | null;
  status: 'unknown' | 'unauthenticated' | 'authenticating' | 'authenticated';
  error: string | null;
  /** Raw E.164 mobile used internally for the verifyOtp API call. */
  pendingMobile: string | null;
  /** Masked version of pendingMobile for display only (e.g. +91••••••210). */
  pendingMaskedMobile: string | null;
  pendingExpiresAt: string | null;
  pendingIsNewUser: boolean | null;
  /** Dev-only: OTP shown on screen when Console provider is active. Always null in production. */
  pendingDevOtp: string | null;

  requestOtp: (mobile: string) => Promise<void>;
  /**
   * A password is mandatory on every OTP verification — the argument order puts it before the
   * optional display name so a call site cannot silently forget it.
   */
  verifyOtp: (
    otp: string,
    password: string,
    purpose: OtpIntent,
    displayName?: string,
  ) => Promise<void>;
  loginWithPassword: (mobile: string, password: string) => Promise<void>;
  refreshSession: () => Promise<boolean>;
  logout: () => Promise<void>;
  hydrateOnBoot: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist<AuthState>(
    (set, get) => ({
      // Persisted
      refreshToken: null,
      refreshTokenExpiresAt: null,
      user: null,

      // In-memory only
      accessToken: null,
      accessTokenExpiresAt: null,
      status: 'unknown',
      error: null,
      pendingMobile: null,
      pendingMaskedMobile: null,
      pendingExpiresAt: null,
      pendingIsNewUser: null,
      pendingDevOtp: null,

      async requestOtp(mobile) {
        set({ error: null });
        try {
          const r = await authApi.requestOtp(mobile);
          set({
            pendingMobile: mobile, // raw E.164 — used by verifyOtp API call
            pendingMaskedMobile: r.maskedMobile, // masked — displayed to the user
            pendingExpiresAt: r.expiresAt,
            pendingIsNewUser: r.isNewUser,
            pendingDevOtp: r.devOtp ?? null,
            error: null,
          });
        } catch (e) {
          set({ error: e instanceof Error ? e.message : 'Failed to send OTP.' });
          throw e;
        }
      },

      async verifyOtp(otp, password, purpose, displayName) {
        const mobile = get().pendingMobile;
        if (!mobile) {
          set({ error: 'No pending OTP — request a new code.' });
          return;
        }
        set({ status: 'authenticating', error: null });
        try {
          // Omit optional fields (rather than passing `undefined`) so we satisfy
          // `exactOptionalPropertyTypes`. Password and purpose are always sent.
          const tokens: AuthTokens = await authApi.verifyOtp({
            mobile,
            otp,
            password,
            purpose,
            ...(displayName !== undefined ? { displayName } : {}),
          });
          applyTokens(set, tokens);
        } catch (e) {
          set({
            status: 'unauthenticated',
            error: e instanceof Error ? e.message : 'OTP verification failed.',
          });
          throw e;
        }
      },

      async loginWithPassword(mobile, password) {
        set({ status: 'authenticating', error: null });
        try {
          const tokens: AuthTokens = await authApi.loginWithPassword({ mobile, password });
          applyTokens(set, tokens);
        } catch (e) {
          set({
            status: 'unauthenticated',
            error: e instanceof Error ? e.message : 'Sign-in failed.',
          });
          throw e;
        }
      },

      async refreshSession() {
        const refresh = get().refreshToken;
        if (!refresh) {
          set({ status: 'unauthenticated' });
          return false;
        }
        try {
          const tokens = await authApi.refresh(refresh);
          applyTokens(set, tokens);
          return true;
        } catch (e) {
          // Reuse-detection / expired refresh → wipe and force re-login
          if (e instanceof ApiError && e.status === 401) {
            set({
              accessToken: null,
              accessTokenExpiresAt: null,
              refreshToken: null,
              refreshTokenExpiresAt: null,
              user: null,
              status: 'unauthenticated',
            });
          }
          return false;
        }
      },

      async logout() {
        const refresh = get().refreshToken;
        if (refresh) {
          try {
            await authApi.logout(refresh);
          } catch {
            // best-effort; we still clear local state
          }
        }
        set({
          accessToken: null,
          accessTokenExpiresAt: null,
          refreshToken: null,
          refreshTokenExpiresAt: null,
          user: null,
          status: 'unauthenticated',
          pendingMobile: null,
          pendingMaskedMobile: null,
          pendingExpiresAt: null,
          pendingIsNewUser: null,
          pendingDevOtp: null,
        });
      },

      async hydrateOnBoot() {
        const refresh = get().refreshToken;
        if (!refresh) {
          set({ status: 'unauthenticated' });
          return;
        }
        const ok = await get().refreshSession();
        if (!ok) {
          set({ status: 'unauthenticated' });
        }
      },

      clearError() {
        set({ error: null });
      },
    }),
    {
      name: 'cscorer.auth',
      // Persist only the long-lived bits; access token must stay in memory.
      partialize: (state) =>
        ({
          refreshToken: state.refreshToken,
          refreshTokenExpiresAt: state.refreshTokenExpiresAt,
          user: state.user,
        }) as unknown as AuthState,
    },
  ),
);

function applyTokens(set: (partial: Partial<AuthState>) => void, tokens: AuthTokens): void {
  set({
    user: tokens.user,
    accessToken: tokens.accessToken,
    accessTokenExpiresAt: tokens.accessTokenExpiresAt,
    refreshToken: tokens.refreshToken,
    refreshTokenExpiresAt: tokens.refreshTokenExpiresAt,
    status: 'authenticated',
    error: null,
    pendingMobile: null,
    pendingMaskedMobile: null,
    pendingExpiresAt: null,
    pendingIsNewUser: null,
    pendingDevOtp: null,
  });
}
